import React from "react";

import { buildOmniStudioGraphFromHandoff, type GuidedStudioOmniStudioImport } from "@/lib/guidedStudio/buildOmniStudioGraphFromHandoff";
import type { OmniStudioHandoffPayload } from "@/lib/guidedStudio/buildOmniStudioHandoffPayload";
import {
  clearStagedOmniStudioHandoffPayload,
  readStagedOmniStudioHandoffPayload,
} from "@/lib/guidedStudio/guidedStudioHandoffStorage";
import {
  onOmniCineClipSelected,
  onOmniStudioSceneSelect,
} from "@/lib/omnistudio/bridge/omnistudioBridgeContract";
import type { OmniStudioGraph } from "@/lib/omnistudio/compiler/directorCompiler.types";
import {
  materializeWorldProposalPromotionSelectionToGraph,
} from "@/lib/omnistudio/compiler/worldNarrative/worldProposalToGraph";
import { mergeWorldProposalIntoGraph } from "@/lib/omnistudio/compiler/worldNarrative/worldProposalGraphMerge";
import { mergeWorldProposalLineage } from "@/lib/omnistudio/compiler/worldNarrative/worldProposalLineage";
import type { PromotionSelectionResult } from "@/lib/omnistudio/compiler/worldNarrative/worldProposalPromotionSelection";
import type { WorldNarrativeCompileProposal } from "@/lib/world/types/worldProposal.types";
import type {
  MergedProposalReviewCandidate,
  WorldProposalAcceptanceSelection,
  WorldProposalLineageRecord,
  WorldProposalReviewSelection,
  WorldProposalStagedVariantRecord,
} from "@/lib/world/review/worldProposalReview.types";

const DEFAULT_GRAPH: OmniStudioGraph = {
  meta: { version: 1, projectId: "omnistudio-v1" },
  nodes: [
    { id: "arc-opening", type: "arc", label: "Opening Arc", x: 84, y: 88 },
    { id: "char-ava", type: "character", name: "Ava", traits: ["focused", "restless"], x: 92, y: 282 },
    { id: "char-ren", type: "character", name: "Ren", traits: ["curious", "steady"], x: 92, y: 410 },
    {
      id: "scene-briefing",
      type: "scene",
      title: "Cold Open Briefing",
      x: 344,
      y: 124,
      durationHintSec: 10,
      participants: ["Ava"],
      mood: "tense",
    },
    {
      id: "scene-crossfade",
      type: "scene",
      title: "Crossfade Pursuit",
      x: 632,
      y: 228,
      durationHintSec: 14,
      participants: ["Ava", "Ren"],
      mood: "kinetic",
    },
    {
      id: "scene-arrival",
      type: "scene",
      title: "Glasshouse Arrival",
      x: 924,
      y: 146,
      durationHintSec: 12,
      participants: ["Ren"],
      mood: "lift",
    },
    { id: "note-turn", type: "note", text: "Land scene marker on the reveal breath.", x: 642, y: 412 },
  ],
  edges: [
    { id: "edge-arc-1", from: "scene-briefing", to: "arc-opening", type: "belongs_to_arc" },
    { id: "edge-arc-2", from: "scene-crossfade", to: "arc-opening", type: "belongs_to_arc" },
    { id: "edge-arc-3", from: "scene-arrival", to: "arc-opening", type: "belongs_to_arc" },
    { id: "edge-order-1", from: "scene-briefing", to: "scene-crossfade", type: "precedes" },
    { id: "edge-order-2", from: "scene-crossfade", to: "scene-arrival", type: "precedes" },
    { id: "edge-char-1", from: "char-ava", to: "scene-briefing", type: "participates" },
    { id: "edge-char-2", from: "char-ava", to: "scene-crossfade", type: "participates" },
    { id: "edge-char-3", from: "char-ren", to: "scene-crossfade", type: "participates" },
    { id: "edge-char-4", from: "char-ren", to: "scene-arrival", type: "participates" },
    { id: "edge-note-1", from: "note-turn", to: "scene-crossfade", type: "relates" },
  ],
};

type OmniStudioGraphContextValue = {
  graph: OmniStudioGraph;
  setGraph: React.Dispatch<React.SetStateAction<OmniStudioGraph>>;
  selectedNodeId: string | null;
  selectNodeById: (nodeId: string | null) => void;
  guidedStudioImport: GuidedStudioOmniStudioImport | null;
  worldProposalLineage: Record<string, WorldProposalLineageRecord>;
  importAcceptedWorldProposal: (
    proposal: WorldNarrativeCompileProposal,
    selection: WorldProposalAcceptanceSelection,
    reviewSelection?: WorldProposalReviewSelection,
    stagedVariant?: WorldProposalStagedVariantRecord,
    mergedCandidate?: MergedProposalReviewCandidate,
    resolvedPromotionSelection?: PromotionSelectionResult,
  ) => void;
};

const OmniStudioGraphContext = React.createContext<OmniStudioGraphContextValue | null>(null);

function deriveInitialSelectedNodeId(graph: OmniStudioGraph): string | null {
  return graph.nodes.find((node) => node.type === "scene")?.id ?? graph.nodes[0]?.id ?? null;
}

export function OmniStudioGraphProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const initialPayloadRef = React.useRef<OmniStudioHandoffPayload | null | undefined>(undefined);
  if (initialPayloadRef.current === undefined) {
    initialPayloadRef.current = readStagedOmniStudioHandoffPayload();
  }

  const initialPayload = initialPayloadRef.current;
  const initialState = React.useMemo(() => {
    if (!initialPayload) {
      return {
        graph: DEFAULT_GRAPH,
        selectedNodeId: deriveInitialSelectedNodeId(DEFAULT_GRAPH),
        guidedStudioImport: null,
      };
    }

    const imported = buildOmniStudioGraphFromHandoff(initialPayload);
    return {
      graph: imported.graph,
      selectedNodeId: imported.selectedNodeId,
      guidedStudioImport: imported,
    };
  }, [initialPayload]);
  const [graph, setGraph] = React.useState<OmniStudioGraph>(initialState.graph);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(initialState.selectedNodeId);
  const [guidedStudioImport] = React.useState<GuidedStudioOmniStudioImport | null>(initialState.guidedStudioImport);
  const [worldProposalLineage, setWorldProposalLineage] = React.useState<Record<string, WorldProposalLineageRecord>>({});

  React.useEffect(() => {
    if (!initialPayload) return;
    clearStagedOmniStudioHandoffPayload();
  }, [initialPayload]);

  const selectNodeById = React.useCallback((nodeId: string | null) => {
    setSelectedNodeId(nodeId);
  }, []);

  const importAcceptedWorldProposal = React.useCallback(
    (
      proposal: WorldNarrativeCompileProposal,
      selection: WorldProposalAcceptanceSelection,
      reviewSelection?: WorldProposalReviewSelection,
      stagedVariant?: WorldProposalStagedVariantRecord,
      mergedCandidate?: MergedProposalReviewCandidate,
      resolvedPromotionSelection?: PromotionSelectionResult,
    ) => {
      const materialized = materializeWorldProposalPromotionSelectionToGraph(
        proposal,
        selection,
        resolvedPromotionSelection?.source === "staged_variant"
          ? {
            preferredSource: "staged_variant",
            stagedVariantId: resolvedPromotionSelection.stagedVariant.variantId,
          }
          : resolvedPromotionSelection?.source === "merged"
            ? {
              preferredSource: "merged",
              mergedId: resolvedPromotionSelection.mergedCandidate.mergedId,
            }
          : reviewSelection,
        resolvedPromotionSelection?.source === "staged_variant"
          ? resolvedPromotionSelection.stagedVariant
          : stagedVariant,
        resolvedPromotionSelection?.source === "merged"
          ? resolvedPromotionSelection.mergedCandidate
          : mergedCandidate,
      );
      if (materialized.nodes.length === 0 && materialized.edges.length === 0) return;

      setGraph((current) => mergeWorldProposalIntoGraph(current, materialized));
      setWorldProposalLineage((current) => mergeWorldProposalLineage(current, materialized.lineage));

      const firstSceneNode = materialized.nodes.find((node) => node.type === "scene");
      if (firstSceneNode) {
        setSelectedNodeId(firstSceneNode.id);
      }
    },
    [],
  );

  React.useEffect(() => {
    const unsubscribeSceneSelect = onOmniStudioSceneSelect((detail) => {
      const sceneId = String(detail?.sceneId ?? "").trim();
      if (!sceneId) return;
      setSelectedNodeId(sceneId);
    });

    const unsubscribeClipSelected = onOmniCineClipSelected((detail) => {
      const clipId = String(detail?.clipId ?? "").trim();
      if (!clipId.startsWith("omnistudio.scene.")) return;
      const sceneId = clipId.slice("omnistudio.scene.".length);
      if (!sceneId) return;
      setSelectedNodeId(sceneId);
    });

    return () => {
      unsubscribeSceneSelect();
      unsubscribeClipSelected();
    };
  }, []);

  const value = React.useMemo<OmniStudioGraphContextValue>(
    () => ({
      graph,
      setGraph,
      selectedNodeId,
      selectNodeById,
      guidedStudioImport,
      worldProposalLineage,
      importAcceptedWorldProposal,
    }),
    [graph, guidedStudioImport, importAcceptedWorldProposal, selectedNodeId, selectNodeById, worldProposalLineage],
  );

  return <OmniStudioGraphContext.Provider value={value}>{children}</OmniStudioGraphContext.Provider>;
}

export function useOmniStudioGraph(): OmniStudioGraphContextValue {
  const value = React.useContext(OmniStudioGraphContext);
  if (!value) throw new Error("useOmniStudioGraph must be used within OmniStudioGraphProvider");
  return value;
}

export function useOptionalOmniStudioGraph(): OmniStudioGraphContextValue | null {
  return React.useContext(OmniStudioGraphContext);
}